import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CombinatoricsCalculator = () => {
  const [inputData, setInputData] = useState({
    calculationType: 'combinations',
    n: '',
    k: '',
    items: '',
    subsetSize: ''
  });

  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateCombinatorics = () => {
    const { calculationType, n, k, items, subsetSize } = inputData;

    // Validate inputs based on calculation type
    try {
      let nNum, kNum, itemsNum, subsetSizeNum;
      
      if (calculationType === 'combinations' || calculationType === 'permutations') {
        if (!n || !k) {
          throw new Error('Please enter values for n and k');
        }
        nNum = parseInt(n);
        kNum = parseInt(k);
        
        if (isNaN(nNum) || isNaN(kNum)) {
          throw new Error('Please enter valid integers for n and k');
        }
        
        if (nNum < 0 || kNum < 0) {
          throw new Error('n and k must be non-negative integers');
        }
        
        if (kNum > nNum) {
          throw new Error('k cannot be greater than n');
        }
      } else if (calculationType === 'subsets') {
        if (!items || !subsetSize) {
          throw new Error('Please enter values for items and subset size');
        }
        itemsNum = parseInt(items);
        subsetSizeNum = parseInt(subsetSize);
        
        if (isNaN(itemsNum) || isNaN(subsetSizeNum)) {
          throw new Error('Please enter valid integers for items and subset size');
        }
        
        if (itemsNum < 0 || subsetSizeNum < 0) {
          throw new Error('Items and subset size must be non-negative integers');
        }
      }

      let resultValue;
      let calculationSteps = [];

      // Calculate based on selected type
      switch (calculationType) {
        case 'combinations':
          resultValue = combinations(nNum, kNum);
          calculationSteps = [
            `Step 1: Use the combinations formula: C(n, k) = n! / (k! * (n - k)!)`,
            `Step 2: Substitute values: C(${nNum}, ${kNum}) = ${nNum}! / (${kNum}! * (${nNum - kNum})!)`,
            `Step 3: Calculate factorials`,
            `Step 4: Divide the factorials to get the result: ${resultValue}`
          ];
          break;
          
        case 'permutations':
          resultValue = permutations(nNum, kNum);
          calculationSteps = [
            `Step 1: Use the permutations formula: P(n, k) = n! / (n - k)!`,
            `Step 2: Substitute values: P(${nNum}, ${kNum}) = ${nNum}! / (${nNum - kNum}!)`,
            `Step 3: Calculate factorials`,
            `Step 4: Divide the factorials to get the result: ${resultValue}`
          ];
          break;
          
        case 'subsets':
          resultValue = Math.pow(2, itemsNum);
          calculationSteps = [
            `Step 1: Use the subsets formula: 2^n`,
            `Step 2: Substitute value: 2^${itemsNum}`,
            `Step 3: Calculate the result: ${resultValue}`
          ];
          break;
          
        default:
          throw new Error('Invalid calculation type selected');
      }

      // Create visualization data
      const chartData = {
        labels: ['Result'],
        datasets: [{
          label: 'Combinatorics Value',
          data: [resultValue],
          backgroundColor: 'rgba(75, 192, 192, 0.5)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }]
      };

      setResult({
        calculationType,
        result: resultValue,
        calculationSteps,
        chartData
      });

    } catch (err) {
      setError(err.message);
    }
  };

  // Utility function to calculate factorial
  const factorial = (n) => {
    if (n === 0 || n === 1) return 1;
    let result = 1;
    for (let i = 2; i <= n; i++) {
      result *= i;
    }
    return result;
  };

  // Calculate combinations (n choose k)
  const combinations = (n, k) => {
    return factorial(n) / (factorial(k) * factorial(n - k));
  };

  // Calculate permutations (n permute k)
  const permutations = (n, k) => {
    return factorial(n) / factorial(n - k);
  };

  const exampleData = {
    combinations: {
      n: 5,
      k: 2,
      result: 10
    },
    permutations: {
      n: 5,
      k: 2,
      result: 20
    },
    subsets: {
      items: 3,
      result: 8
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-blue-100 to-purple-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Combinatorics Calculator | Combinations and Permutations</title>
          <meta name="description" content="Calculate combinations, permutations, and subsets using this combinatorics calculator. This tool helps solve counting problems in mathematics and statistics." />
          <meta name="keywords" content="combinatorics calculator, combinations, permutations, subsets, math calculator, statistics calculator" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-blue-600 mr-4">🧮</div>
            <h1 className="text-3xl font-bold text-gray-800">Combinatorics Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate combinations, permutations, and subsets using this combinatorics calculator. This tool helps solve counting problems in mathematics and statistics.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Calculation Type:</label>
              <select
                name="calculationType"
                value={inputData.calculationType}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="combinations">Combinations (n choose k)</option>
                <option value="permutations">Permutations (n permute k)</option>
                <option value="subsets">Subsets (power set)</option>
              </select>
            </div>

            {inputData.calculationType === 'combinations' || inputData.calculationType === 'permutations' ? (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Total Items (n):</label>
                  <input
                    type="number"
                    name="n"
                    value={inputData.n}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter total number of items"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Choose (k):</label>
                  <input
                    type="number"
                    name="k"
                    value={inputData.k}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter number of items to choose"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Number of Items:</label>
                  <input
                    type="number"
                    name="items"
                    value={inputData.items}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter number of items"
                  />
                </div>
                <div className="input-group">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subset Size:</label>
                  <input
                    type="number"
                    name="subsetSize"
                    value={inputData.subsetSize}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter subset size"
                  />
                </div>
              </>
            )}
          </div>

          <button
            onClick={calculateCombinatorics}
            className="w-full py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {result && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Summary</h3>
                  <p className="text-lg">Calculation Type: {result.calculationType}</p>
                  {result.calculationType === 'combinations' && (
                    <>
                      <p className="text-lg">n = {inputData.n}</p>
                      <p className="text-lg">k = {inputData.k}</p>
                    </>
                  )}
                  {result.calculationType === 'subsets' && (
                    <>
                      <p className="text-lg">Items = {inputData.items}</p>
                      <p className="text-lg">Subset Size = {inputData.subsetSize}</p>
                    </>
                  )}
                  <p className="text-2xl font-bold text-blue-600 mt-4">Result: {result.result}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Visualization</h3>
                  <div className="h-48 bg-white rounded-lg flex items-center justify-center">
                    <Bar
                      data={result.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: 'Result Value'
                            }
                          },
                          x: {
                            title: {
                              display: true,
                              text: 'Calculation'
                            }
                          }
                        },
                        plugins: {
                          title: {
                            display: true,
                            text: 'Combinatorics Result'
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {result.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for combinations:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Calculation Type: Combinations</li>
                <li>Total Items (n): {exampleData.combinations.n}</li>
                <li>Choose (k): {exampleData.combinations.k}</li>
                <li>Result: {exampleData.combinations.result}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Use the combinations formula: C(n, k) = n! / (k! * (n - k)!)</li>
                <li>Substitute values: C(5, 2) = 5! / (2! * 3!)</li>
                <li>Calculate factorials: 120 / (2 * 6) = 10</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Combinatorics Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Select Calculation Type:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose between combinations, permutations, or subsets</li>
                </ul>
              </li>
              <li><strong>Enter Required Values:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>For combinations and permutations: enter n (total items) and k (items to choose)</li>
                  <li>For subsets: enter the number of items and subset size</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The result of the combinatorial calculation</li>
                  <li>A visual representation of the result</li>
                  <li>Detailed calculation steps</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Combinations count selections where order doesn't matter</li>
                <li>Permutations count selections where order matters</li>
                <li>Subsets count all possible groupings of items</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Combinatorics <span className="text-blue-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Combinatorics is a branch of mathematics that deals with counting, arranging, and selecting items from collections. The three main concepts covered by this calculator are:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Combinations:</strong> The number of ways to choose k items from a set of n items without regard to order</li>
              <li><strong>Permutations:</strong> The number of ways to arrange k items from a set of n items where order matters</li>
              <li><strong>Subsets:</strong> The total number of possible subsets (including the empty set and the set itself) of a set with n elements</li>
            </ul>
            <p className="mt-4">
              These concepts are widely used in:
              <ul className="list-disc pl-6 mt-4">
                <li><strong>Probability:</strong> Calculating likelihoods of events</li>
                <li><strong>Statistics:</strong> Sampling and data analysis</li>
                <li><strong>Computer Science:</strong> Algorithm design and analysis</li>
                <li><strong>Game Theory:</strong> Strategy development</li>
              </ul>
            </p>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Understand the Problem:</strong> Determine if order matters in your selection</li>
              <li><strong>Use Appropriate Formula:</strong> Select combinations, permutations, or subsets based on the problem requirements</li>
              <li><strong>Validate Results:</strong> Double-check calculations for critical applications</li>
              <li><strong>Consider Limitations:</strong> These formulas assume selections without replacement</li>
            </ul>
            <p className="italic mt-4">
              Note: For problems with replacement or more complex constraints, consider advanced combinatorial methods.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CombinatoricsCalculator;